import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, catchError, finalize, tap } from 'rxjs';
import { NuggetComment } from '@shared/models/nugget-comment.model';
import { saveBlob } from '@shared/utils/file-download.utils';
import { NotificationService } from '@shared/services/notification.service';
import { NuggetInteractionService } from '@shared/services/nugget-interaction.service';
import { NuggetService } from '@shared/services/nugget.service';
import {
    AddNuggetComment,
    DeleteNuggetComment,
    DownloadNuggetDocument,
    LoadNuggetComments
} from './nugget-interaction.actions';
import { NuggetInteractionStateModel } from './nugget-interaction.model';

@State<NuggetInteractionStateModel>({
    name: 'nuggetInteraction',
    defaults: {
        comments: {},
        loadingNuggetIds: [],
        submittingNuggetIds: [],
        downloadingNuggetIds: []
    }
})
@Injectable()
export class NuggetInteractionState {
    private readonly interactionService = inject(NuggetInteractionService);
    private readonly nuggetService = inject(NuggetService);
    private readonly notificationService = inject(NotificationService);

    @Selector()
    static comments(state: NuggetInteractionStateModel): Record<string, NuggetComment[]> {
        return state.comments;
    }

    @Selector()
    static loadingNuggetIds(state: NuggetInteractionStateModel): string[] {
        return state.loadingNuggetIds;
    }

    @Selector()
    static submittingNuggetIds(state: NuggetInteractionStateModel): string[] {
        return state.submittingNuggetIds;
    }

    @Selector()
    static downloadingNuggetIds(state: NuggetInteractionStateModel): string[] {
        return state.downloadingNuggetIds;
    }

    @Action(LoadNuggetComments)
    public loadComments(
        ctx: StateContext<NuggetInteractionStateModel>,
        { payload }: LoadNuggetComments
    ): Observable<NuggetComment[]> {
        const { nugget } = payload;
        if (ctx.getState().loadingNuggetIds.includes(nugget.id)) {
            return EMPTY;
        }

        this.toggleId(ctx, 'loadingNuggetIds', nugget.id, true);

        return this.interactionService.loadComments(nugget.id).pipe(
            tap(comments => this.setComments(ctx, nugget.id, comments)),
            catchError(() => {
                this.notificationService.showError('Kommentare konnten nicht geladen werden');
                return EMPTY;
            }),
            finalize(() => this.toggleId(ctx, 'loadingNuggetIds', nugget.id, false))
        );
    }

    @Action(AddNuggetComment)
    public addComment(
        ctx: StateContext<NuggetInteractionStateModel>,
        { payload }: AddNuggetComment
    ): Observable<NuggetComment> {
        const { nugget, text } = payload;

        this.toggleId(ctx, 'submittingNuggetIds', nugget.id, true);

        return this.interactionService.addComment(nugget.id, text).pipe(
            tap(comment => this.setComments(ctx, nugget.id, [...this.commentsOf(ctx, nugget.id), comment])),
            catchError(() => {
                this.notificationService.showError('Kommentar konnte nicht gespeichert werden');
                return EMPTY;
            }),
            finalize(() => this.toggleId(ctx, 'submittingNuggetIds', nugget.id, false))
        );
    }

    @Action(DeleteNuggetComment)
    public deleteComment(
        ctx: StateContext<NuggetInteractionStateModel>,
        { payload }: DeleteNuggetComment
    ): Observable<void> {
        const { nugget, comment } = payload;

        return this.interactionService.deleteComment(nugget.id, comment.id).pipe(
            tap(() =>
                this.setComments(
                    ctx,
                    nugget.id,
                    this.commentsOf(ctx, nugget.id).filter(entry => entry.id !== comment.id)
                )
            ),
            catchError(() => {
                this.notificationService.showError('Kommentar konnte nicht gelöscht werden');
                return EMPTY;
            })
        );
    }

    @Action(DownloadNuggetDocument)
    public downloadDocument(
        ctx: StateContext<NuggetInteractionStateModel>,
        { payload }: DownloadNuggetDocument
    ): Observable<Blob> {
        const { nugget } = payload;
        const url = nugget.link;
        if (!url || ctx.getState().downloadingNuggetIds.includes(nugget.id)) {
            return EMPTY;
        }

        const fileName = nugget.documentName ?? nugget.name;
        this.toggleId(ctx, 'downloadingNuggetIds', nugget.id, true);

        return this.nuggetService.downloadDocument(url).pipe(
            tap(blob => saveBlob(blob, fileName)),
            catchError(() => {
                this.notificationService.showError('Dokument konnte nicht heruntergeladen werden', fileName);
                return EMPTY;
            }),
            finalize(() => this.toggleId(ctx, 'downloadingNuggetIds', nugget.id, false))
        );
    }

    private commentsOf(ctx: StateContext<NuggetInteractionStateModel>, nuggetId: string): NuggetComment[] {
        return ctx.getState().comments[nuggetId] ?? [];
    }

    private setComments(
        ctx: StateContext<NuggetInteractionStateModel>,
        nuggetId: string,
        comments: NuggetComment[]
    ): void {
        ctx.patchState({ comments: { ...ctx.getState().comments, [nuggetId]: comments } });
    }

    private toggleId(
        ctx: StateContext<NuggetInteractionStateModel>,
        key: 'loadingNuggetIds' | 'submittingNuggetIds' | 'downloadingNuggetIds',
        nuggetId: string,
        active: boolean
    ): void {
        const current = ctx.getState()[key];
        ctx.patchState({
            [key]: active ? [...new Set([...current, nuggetId])] : current.filter(id => id !== nuggetId)
        });
    }
}
